import React, { useCallback } from 'react';
import { useReservation } from './reservation.context';
import { useValuePartialUpdate } from '../../hooks/useChangeEventUpdate';
import { useAuthUsers } from '../../auth/auth.context';
import { PaymentReceipts } from './paymentReceipts.component';
import { Box, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';

/**
 * Formulaire de gestion des remboursements
 * 
 * Ce composant :
 * - Affiche la liste des remboursements existants
 * - Permet l'ajout de nouveaux remboursements
 * - Utilise le même composant PaymentReceipts pour l'affichage
 * 
 * Logique métier :
 * - Création de nouveaux remboursements avec informations utilisateur
 * - Gestion séparée des remboursements par rapport aux encaissements
 * - Utilisation des informations de l'utilisateur connecté
 * 
 * @returns Composant React du formulaire de remboursements
 */
export const PaymentRefundsForm: React.FC = () => {
  const { reservation, update } = useReservation();
  const { loggedUser } = useAuthUsers();

  const updatePayment = useValuePartialUpdate<CRM.ReservationDocument>(
    'payment',
    update,
  );

  const onChange = useCallback(
    newReceipts =>
      updatePayment({ ...reservation.payment, refunds: newReceipts } as any),
    [updatePayment, reservation.payment],
  );

  const addRefund = useCallback(() => {
    const newReceipts: CRM.PaymentReceipt[] = [
      ...(reservation.payment?.refunds || []),
      {
        amountInEuro: 0,
        method: 'cash',
        userGroupId: loggedUser?.userGroup,
        userId: loggedUser?._id,
        date: new Date(),
      },
    ];
    onChange(newReceipts);
  }, [onChange, loggedUser, reservation.payment?.refunds]);

  return (
    <PaymentReceipts
      value={reservation.payment?.refunds || []}
      onChange={onChange}
    >
      <Box mt={1}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={addRefund}
        >
          Ajouter un remboursement
        </Button>
      </Box>
    </PaymentReceipts>
  );
};
